/**
 * 脚本节点卡片外侧附属 UI（脚本节点开发方案 §1.1 完成态 / 参考图「生成后的节点状态.png」）：
 * 顶部工具条：重新生成（可点）/ 批量生成分镜 / 批量生视频（§8-4 批量接线，按就绪度启用）/ 下载（导出脚本 JSON）；
 *   v1.4.13 样式与挂载时机对齐图片节点 TopFunctionBar——仅选中态出现、rounded-xl 容器 + h-9 按钮同款视觉语言；
 * 底部补充输入栏：+参考图（P2 接线前置灰）/ 上游文本引用块 / 剧情描述输入（发送 = 以输入文本为补充指令重新生成）/ LLM 模型下拉；
 *   v1.4.16 草稿提升 GenerateNode 不随面板卸载丢失、默认模型 Gemini 3.1 Flash Lite（对话）、模型名前⚡移除、右侧⚡改为本次生成积分（积分系统 P1 实装：单价 = 当前模型定价）。
 *   v1.4.17 右上角全屏按钮生效：点击后 createPortal 到 body 的 fixed 居中展开面板（标题 + 大编辑区 + 同款底栏，脱离画布缩放/平移坐标系），
 *     再点 Minimize2 还原回内嵌底部面板；右下角手柄拖拽调整展开面板尺寸（min 520×360，max 视口 92%，对齐参考图一/图二）。
 *   v1.4.19 剧情描述输入与其他节点底部面板统一为同一套 PromptEditor（contenteditable + @ mention）：
 *     @ 候选 = 上游文本节点（TEXT_NODE/PROMPT_INPUT，与引用块同源），插入 @[nodeId|标题] 可视化标签，发送时转 @标题；
 *     发送 = Ctrl/Cmd+Enter 或发送按钮（原 Enter 直发改为换行，与其他面板一致）。
 *   v1.4.20 展开态背景遮罩：全屏时四周压暗（bg-black/50）+ 毛玻璃模糊（backdrop-blur-md），营造面板独立模态显示；
 *     点击遮罩区域还原为内嵌底部面板（与 Minimize2 同效，遮罩拦截点击、画布不被误触）。
 *   v1.4.21 模型下拉修复与样式：外点关闭改 capture 阶段监听（面板根 onMouseDown stopPropagation 阻断 bubble，
 *     曾导致点击面板内其他区域收不起下拉），点击底部面板任意区域（含编辑区）均可收起；下拉菜单字体 text-white/80 → 纯白 text-white。
 *   v1.4.22 翻译按钮接线（此前为纯 UI 占位无 onClick，点击无反应）：中英互译·自动方向（用户拍板）——
 *     中文译英文、英文译中文、混合/其他语言统一译中文，译文替换输入框草稿；走任务管线 LLM 分支
 *     （feature=script-translate·后端白名单两处 + executor result.data={text} 分支），modelId 显式携带面板当前所选对话模型
 *     （路由层对空 modelId 有 400 拦截且未豁免 LLM feature，绕开该区域）；不入恢复链（短任务即弃），翻译中禁用发送防原文误发。
 *   v1.4.23 翻译执行状态存活修复（用户反馈：执行中收起底部面板再展开，转圈消失但后台仍在轮询）：
 *     translating 由组件 useState 提升至模块级 zustand store（按节点 id）——面板随取消选中卸载曾致状态复位，
 *     spinner 与发送守卫一并失效、防重入失效（可重复点出第二个翻译任务）；store 存活于卸载周期之外，
 *     重开面板自动恢复执行中状态；译文写回（GenerateNode 常驻 setState）本就有效，未动。
 *   v1.4.24 翻译/尺寸工具上移共享设施 hooks/useNodeTranslate（与其他节点底部参数面板 EditorPanel 共用）：
 *     翻译执行状态 store、翻译函数、对话模型三级回退（isChatModel/pickTranslateModel）、展开面板尺寸
 *     clampExpandPanelSize 均改为引用共享设施，本面板行为不变（纯重构）。
 * 由 GenerateNode 渲染在根 div（卡片容器之外，InverseScaleBox 反向缩放），不受卡片 overflow-hidden 裁剪。
 */
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  RefreshCw, Clapperboard, Video, Download, Plus, Maximize2, Minimize2,
  Languages, FileText, ChevronDown, Loader2,
} from 'lucide-react';
import { AppNode, NodeType } from '@/types';
import { readScript, Shot } from '@/types/scriptNode';
import { useAgentStore } from '@/stores/agentStore';
import { useNodeStore } from '@/stores/nodeStore';
import { PromptEditor } from '../editor/PromptEditor';
import { findStoryboardChildren, isStoryboardEligibleShot } from '@/components/ScriptPanel/useScriptBatchGenerator';
// v1.4.84 批量确认弹窗（样式对齐用户参考图一/图二）+ 参数类型
import { BatchStoryboardModal, BatchVideoModal } from '@/components/ScriptPanel/BatchGenerateModals';
import type { BatchImageOptions, BatchVideoOptions, BatchVideoItem } from '@/components/ScriptPanel/BatchGenerateModals';
import { useNodeTranslate, isChatModel, pickTranslateModel, clampExpandPanelSize } from '../../hooks';
import { cancelNodeGeneration } from '@/hooks/useGenerationOrchestrator';
import { getModelCost } from '@/utils/credits';

/** v1.4.19 PromptEditor 必填回调占位（onPromptSubmit blur 钩子 / onUpdate / 面板状态钩子在脚本面板无消费场景） */
const noop = () => {};

interface ScriptNodeToolbarProps {
  data: AppNode;
  isWorking: boolean;
  /** SCRIPT/CHARACTER 来源：重新走生成链路 */
  onRegenerate: () => void;
  /** MANUAL 来源：重新生成 = 打开编辑面板 */
  onOpenPanel: () => void;
  /** 补充输入栏发送（输入文本作为 promptOverride 重新生成） */
  onSend: (text: string) => void;
  onUpdateModel: (modelId: string) => void;
  /** §8-4 批量生成分镜（v1.4.84 弹窗确认后回调：携带勾选镜头 + 弹窗底栏生图参数） */
  onBatchStoryboard: (selected: Shot[], options: BatchImageOptions) => void;
  /** §8-4 批量生视频（v1.4.84 弹窗确认后回调：携带勾选分镜图节点 id + 每镜时长/运动提示词/视频参数） */
  onBatchVideos: (selectedNodeIds: string[], options: BatchVideoOptions) => void;
}

/** 顶部工具条：重新生成 / 批量生成分镜 / 批量生视频 / 下载 */
export const ScriptNodeToolbar: React.FC<ScriptNodeToolbarProps> = memo(({
  data,
  isWorking,
  onRegenerate,
  onOpenPanel,
  onSend,
  onUpdateModel,
  onBatchStoryboard,
  onBatchVideos,
}) => {
  const script = readScript((data.data as { script?: unknown }).script);
  const isManual = script?.source === 'MANUAL';

  // §8-4 批量按钮就绪度：分镜子节点实时反查（订阅 nodes），镜头提示词就绪度从脚本数据推导
  const nodes = useNodeStore((s) => s.nodes);
  const storyboardChildren = useMemo(
    () => findStoryboardChildren(nodes, data.id),
    [nodes, data.id],
  );
  const eligibleShotCount = useMemo(
    () => (script?.shots ?? []).filter(isStoryboardEligibleShot).length,
    [script],
  );
  // 批量生成分镜：有可用镜头提示词且尚未生成分镜（防重复堆积）
  const canBatchStoryboard = eligibleShotCount > 0 && storyboardChildren.length === 0;
  // 批量生视频：存在已出图的分镜节点
  const canBatchVideos = storyboardChildren.some((n) => !!(n.data as { image?: string }).image);
  const batchStoryboardTitle = storyboardChildren.length > 0
    ? '已生成分镜，删除旧分镜节点后可重新生成'
    : (script?.shots.length ?? 0) > 0
      ? '镜头提示词就绪后可用'
      : '请先在脚本面板添加镜头';

  // v1.4.84 批量确认弹窗：开合状态（点击顶部栏按钮先弹面板，确认后才真正批量创建）
  const [batchModal, setBatchModal] = useState<null | 'storyboard' | 'videos'>(null);

  // 分镜弹窗数据 = 提示词就绪的镜头（行文本取 shot.prompt 分镜提示词，弹窗内空行以画面描述兜底展示）
  const storyboardShots = useMemo(
    () => (script?.shots ?? []).filter(isStoryboardEligibleShot),
    [script],
  );

  // 视频弹窗数据 = 已出图分镜图节点 → BatchVideoItem：镜头序号优先从节点标题「分镜 N」解析
  // （generateBatchStoryboard 创建时写入，防用户增删节点后错位），解析失败按创建序兜底；
  // v1.4.95 运动提示词/画面描述从 script.shots 按 index 匹配（行摘要 = 画面描述对齐生图弹窗，
  // 展开区 = 运动提示词经 AssetMentionText 渲染 @高亮/缩略图，随行携带脚本资产表），
  // 确认后运动提示词写入视频节点 prompt
  const videoItems = useMemo<BatchVideoItem[]>(() => {
    const shotByIndex = new Map((script?.shots ?? []).map((s) => [s.index, s]));
    const assets = script?.assets ?? [];
    return storyboardChildren
      .filter((n) => !!(n.data as { image?: string }).image)
      .map((n, i) => {
        const m = /^分镜\s*(\d+)$/.exec(n.title || '');
        const idx = m ? parseInt(m[1], 10) : i + 1;
        const shot = shotByIndex.get(idx);
        return {
          nodeId: n.id,
          shotIndex: idx,
          description: shot?.description?.trim() ?? '',
          motionPrompt: shot?.motionPrompt?.trim() ?? '',
          assets,
        };
      });
  }, [storyboardChildren, script]);

  const handleDownload = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const s = readScript((data.data as { script?: unknown }).script);
    if (!s) return;
    const blob = new Blob([JSON.stringify(s, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${s.title || '分镜脚本'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [data.data]);

  return (
    <div
      className="nodrag flex items-center gap-2 bg-[#262626]/95 backdrop-blur-xl border border-white/10 rounded-xl px-3 py-1.5 shadow-lg"
      onMouseDown={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        disabled={isWorking}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          if (isManual) onOpenPanel();
          else onRegenerate();
        }}
        className="flex items-center gap-[3px] h-9 px-2.5 rounded-md bg-transparent text-white hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer"
      >
        <RefreshCw size={16} className={isWorking ? 'animate-spin' : ''} />
        <span className="text-sm font-medium whitespace-nowrap">重新生成</span>
      </button>

      {/* §8-4 批量生成分镜（v1.4.84）：先弹确认面板展示各镜分镜提示词，确认后批量创建 IMAGE_GENERATOR 子节点 */}
      <button
        type="button"
        disabled={!canBatchStoryboard}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setBatchModal('storyboard');
        }}
        title={batchStoryboardTitle}
        className={`flex items-center gap-[3px] h-9 px-2.5 rounded-md bg-transparent transition-all ${
          canBatchStoryboard ? 'text-white hover:bg-white/10 cursor-pointer' : 'text-white/40 cursor-not-allowed'
        }`}
      >
        <Clapperboard size={16} />
        <span className="text-sm font-medium whitespace-nowrap">批量生成分镜</span>
      </button>

      {/* §8-4 批量生视频（v1.4.84）：先弹确认面板展示各镜视频运动提示词，确认后批量创建 VIDEO_GENERATOR */}
      <button
        type="button"
        disabled={!canBatchVideos}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setBatchModal('videos');
        }}
        title={canBatchVideos ? '对已出图的分镜批量创建视频节点' : '分镜图生成后可用'}
        className={`flex items-center gap-[3px] h-9 px-2.5 rounded-md bg-transparent transition-all ${
          canBatchVideos ? 'text-white hover:bg-white/10 cursor-pointer' : 'text-white/40 cursor-not-allowed'
        }`}
      >
        <Video size={16} />
        <span className="text-sm font-medium whitespace-nowrap">批量生视频</span>
      </button>

      <button
        type="button"
        onClick={handleDownload}
        title="下载脚本"
        className="flex items-center justify-center w-9 h-9 rounded-md bg-transparent text-white hover:bg-white/10 transition-all cursor-pointer"
      >
        <Download size={16} />
      </button>

      {/* v1.4.84 批量确认弹窗（ModalShell 内部 createPortal 到 body，脱离画布缩放/平移坐标系） */}
      {batchModal === 'storyboard' && (
        <BatchStoryboardModal
          shots={storyboardShots}
          assets={script?.assets ?? []}
          onClose={() => setBatchModal(null)}
          onConfirm={(selected, options) => {
            setBatchModal(null);
            onBatchStoryboard(selected, options);
          }}
        />
      )}
      {batchModal === 'videos' && (
        <BatchVideoModal
          items={videoItems}
          onClose={() => setBatchModal(null)}
          onConfirm={(selectedNodeIds, options) => {
            setBatchModal(null);
            onBatchVideos(selectedNodeIds, options);
          }}
        />
      )}
    </div>
  );
});
ScriptNodeToolbar.displayName = 'ScriptNodeToolbar';

/** v1.4.17 展开面板尺寸边界与 v1.4.23 翻译执行状态 store 已上移共享设施 hooks/useNodeTranslate（v1.4.24） */

/** 底部补充输入栏：+参考图 / 上游文本引用块 / 剧情描述输入 / 模型下拉 / 发送（无批量按钮，剔除批量回调）；
 * v1.4.16 草稿 state 提升至 GenerateNode（镜像图片节点 EditorPanel 的 localPrompt 模式）——面板随取消选中卸载后输入不丢 */
export const ScriptQuickInput: React.FC<Omit<ScriptNodeToolbarProps, 'onBatchStoryboard' | 'onBatchVideos'> & {
  draftText: string;
  onDraftChange: (text: string) => void;
}> = memo(({
  data,
  isWorking,
  draftText,
  onDraftChange,
  onSend,
  onUpdateModel,
}) => {
  const text = draftText;
  const setText = onDraftChange;
  const [modelOpen, setModelOpen] = useState(false);
  const modelMenuRef = useRef<HTMLDivElement>(null);

  // v1.4.24 翻译接线（v1.4.22 接线 / v1.4.23 执行状态存活机制均已上移共享设施）：执行状态按节点 id 存
  // 共享模块级 store，跨面板卸载周期存活；翻译期间禁用发送（防原文被误发）
  const nodeId = data.id;

  // v1.4.17 展开态与面板尺寸（右下角手柄拖拽调整）；组件随取消选中卸载，expanded 自动复位为内嵌模式
  const [expanded, setExpanded] = useState(false);
  const [panelSize, setPanelSize] = useState(() => clampExpandPanelSize(800, 520));
  const panelSizeRef = useRef(panelSize);
  panelSizeRef.current = panelSize;

  // 展开态下窗口缩小时兜底 clamp，保证面板永不超出视口
  useEffect(() => {
    if (!expanded) return;
    const onResize = () => setPanelSize((s) => clampExpandPanelSize(s.w, s.h));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [expanded]);

  // 右下角手柄：mousedown 记起点 → window mousemove 按增量 clamp 更新尺寸 → mouseup 收尾
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const { w: startW, h: startH } = panelSizeRef.current;
    const onMove = (ev: MouseEvent) => {
      setPanelSize(clampExpandPanelSize(startW + (ev.clientX - startX), startH + (ev.clientY - startY)));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  const models = useAgentStore((s) => s.availableModels);
  const ensureAvailableModels = useAgentStore((s) => s.ensureAvailableModels);
  const nodes = useNodeStore((s) => s.nodes);

  useEffect(() => { ensureAvailableModels(); }, [ensureAvailableModels]);

  // v1.4.21 点击外部关闭模型下拉（capture 阶段监听）：面板根节点 onMouseDown stopPropagation 会阻断
  // bubble 至 document，导致点击面板内其他区域收不起下拉——capture 自 document 向下捕获、先于 React 合成事件触发，不受阻断影响
  useEffect(() => {
    if (!modelOpen) return;
    const onDown = (e: MouseEvent) => {
      if (modelMenuRef.current && !modelMenuRef.current.contains(e.target as Node)) {
        setModelOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [modelOpen]);

  // 上游文本类节点引用块（TEXT_NODE / PROMPT_INPUT，与图三引用小块对应）
  const upstreamTextNodes = useMemo(() => nodes.filter(
    (n) => data.inputs.includes(n.id) && (n.type === NodeType.TEXT_NODE || n.type === NodeType.PROMPT_INPUT),
  ), [nodes, data.inputs]);
  const upstreamCards = upstreamTextNodes.map((n, i) => (
    <div
      key={n.id}
      title={n.title}
      className="relative w-14 h-14 shrink-0 rounded-lg bg-white/[0.06] border border-white/10 flex items-center justify-center"
    >
      <FileText size={20} className="text-white/40" />
      <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-zinc-600 text-[10px] text-white">
        {i + 1}
      </span>
    </div>
  ));

  // v1.4.17 展开态顶部标题（图一左上角）：脚本标题 → 节点标题 → 兜底
  const scriptTitle = readScript((data.data as { script?: unknown }).script)?.title
    || data.title || '分镜脚本';

  const chatModels = useMemo(() => models.filter(isChatModel), [models]);

  // v1.4.16 默认模型（参考图：Gemini 3.1 Flash Lite（对话））：节点未显式选型时按三级回退写入
  // （v1.4.24 回退逻辑收口共享设施 pickTranslateModel，EditorPanel 翻译模型同源）
  useEffect(() => {
    if (!data.data.model && chatModels.length > 0) {
      const fallbackModel = pickTranslateModel(chatModels);
      if (fallbackModel) onUpdateModel(fallbackModel.id);
    }
  }, [chatModels, data.data.model, onUpdateModel]);

  const currentModelId = data.data.model || '';
  const currentModelName = chatModels.find((m) => m.id === currentModelId)?.name
    || (currentModelId || '默认模型');

  // v1.4.24 翻译（中英互译·自动方向）：走共享设施 useNodeTranslate（EditorPanel 同款），走任务管线 LLM 分支
  // （feature=script-translate），译文替换输入框草稿；不入恢复链（短任务即弃，失败可直接重译）
  const { translating, translate: handleTranslate } = useNodeTranslate({
    nodeId,
    text,
    modelId: currentModelId,
    onResult: setText,
  });

  // v1.4.22 翻译中禁用发送（canSend 守卫），防翻译期间原文被误发；
  // v1.4.31 已连上游文本节点（剧本等）时输入框为空也允许发送：生成链路（useGenerationOrchestrator）
  // 执行时无条件前置拼接上游文案，空输入 + 有上游 = 纯上游文案生成
  const canSend = !isWorking && !translating && (text.trim().length > 0 || upstreamTextNodes.length > 0);

  // v1.4.19 发送前把可视化引用标记转为 LLM 可读形式：@[nodeId|标题] → @标题
  // （上游文本内容已由生成链路自动前置拼接，标记仅起位置指引作用）
  const handleSend = useCallback(() => {
    if (!canSend) return;
    const sendText = text.replace(/@\[[^\]|]+\|([^\]]*)\]/g, (_, title) => `@${title}`).trim();
    onSend(sendText);
    setText('');
  }, [canSend, onSend, text, setText]);

  // v1.4.19 @ 引用候选 = 上游文本节点（TEXT_NODE / PROMPT_INPUT，与引用块同源）；
  // 文本节点无图，标签仅渲染标题（PromptEditor v1.4.19 起支持无图候选）
  const mentionCandidates = useMemo(() => upstreamTextNodes.map((n) => ({
    id: n.id,
    title: n.title || '文本',
    src: (n.data as { image?: string }).image || '',
  })), [upstreamTextNodes]);

  // v1.4.19 统一输入区（与其他节点底部面板同一套 PromptEditor）：发送 = Ctrl/Cmd+Enter 或点击发送按钮
  const editorProps = {
    nodeId: data.id,
    nodeType: NodeType.SCRIPT_NODE,
    prompt: text,
    inputAssets: [],
    isWorking,
    currentMode: '',
    onPromptChange: setText,
    onPromptSubmit: noop,
    onUpdate: noop,
    onSetCurrentMode: noop,
    onSetIsInputFocused: noop,
    onGenerate: handleSend,
    mentionCandidates,
  };

  // v1.4.17 底部工具栏两态复用：模型下拉 / 翻译 / 积分 / 发送（内嵌与展开同构）
  const footer = (
    <div className="flex items-center justify-between">
      <div className="relative" ref={modelMenuRef}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setModelOpen((v) => !v);
          }}
          className="flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-white/10 text-[13px] text-white/70 transition-colors"
        >
          <span className="max-w-[180px] truncate">{currentModelName}</span>
          <ChevronDown size={14} className="text-white/40" />
        </button>
        {modelOpen && (
          <div className="absolute bottom-full mb-1 left-0 w-60 max-h-64 overflow-auto bg-[#262626] border border-white/10 rounded-xl shadow-xl py-1 z-50">
            {chatModels.length === 0 && (
              <div className="px-3 py-2 text-[12px] text-white/40">暂无可用模型</div>
            )}
            {chatModels.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onUpdateModel(m.id);
                  setModelOpen(false);
                }}
                className={`w-full flex items-center justify-between px-3 py-2 text-[13px] hover:bg-white/10 transition-colors ${
                  m.id === currentModelId ? 'text-blue-300' : 'text-white'
                }`}
              >
                <span className="truncate">{m.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-3">
        {/* v1.4.22 翻译：中英互译·自动方向，译文替换输入框草稿；翻译中转 Loader2 防重入 */}
        <button
          type="button"
          disabled={translating || !text.trim() || !currentModelId}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleTranslate();
          }}
          title={currentModelId ? '翻译（中英互译）' : '暂无可用模型'}
          className="text-white/40 hover:text-white/70 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {translating ? <Loader2 size={16} className="animate-spin" /> : <Languages size={16} />}
        </button>
        {/* v0.35 积分图标改用顶部积分胶囊同款 token-icon.svg（原 Zap）；单价 = 当前模型定价（未配置 = 0） */}
        <span
          className="flex items-center gap-1 text-[12px] text-white/40"
          title="本次生成预计消耗积分"
        >
          <img src="/icons/home/token-icon.svg" alt="" width={12} height={12} draggable={false} />
          {/* 预留三位数积分宽度（tabular-nums 等宽数字），位数变化不引起布局跳动 */}
          <span className="min-w-[22px] tabular-nums text-left">{getModelCost(currentModelId, models)}</span>
        </span>
        {/* v1.4.25 取消生成：生成中发送按钮原地切换为停止按钮（白底规格不变，图标=实心圆角红色方块，点击取消），与其他底部参数面板一致 */}
        {isWorking ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              cancelNodeGeneration(nodeId);
            }}
            title="取消生成"
            className="flex items-center justify-center w-8 h-8 rounded-[8px] bg-white transition-all cursor-pointer hover:scale-105"
          >
            <svg width={16} height={16} viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <rect x="3" y="3" width="10" height="10" rx="2.5" fill="#ef4444" />
            </svg>
          </button>
        ) : (
          /* v1.4.11：发送按钮统一为图三样式——圆角方形白底 + send-arrow.svg（同 MessageInput 规格 32×32/8px） */
          <button
            type="button"
            disabled={!canSend}
            onClick={handleSend}
            title="发送"
            className="flex items-center justify-center w-8 h-8 rounded-[8px] bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            <img src="/icons/send-arrow.svg" alt="发送" width={16} height={16} style={{ objectFit: 'contain' }} draggable={false} />
          </button>
        )}
      </div>
    </div>
  );

  // v1.4.17 展开态（图一）：createPortal 到 body 的 fixed 居中面板——DOM 脱离 React Flow transform 缩放/平移上下文，
  // React 树仍挂在本组件内（展开尺寸等 state 不丢）；结构 = 标题行 + 大编辑区 + 同款底栏 + 右下角拖拽手柄
  if (expanded) {
    return createPortal(
      <>
        {/* v1.4.20 背景遮罩：四周压暗 + 毛玻璃模糊，营造面板独立模态显示；点击遮罩还原为内嵌底部面板 */}
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-md"
          style={{ zIndex: 89 }}
          onClick={() => setExpanded(false)}
        />
      <div
        className="nodrag flex flex-col rounded-2xl bg-[#262626]/[0.97] backdrop-blur-xl border border-white/10 shadow-2xl p-5"
        style={{
          position: 'fixed',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: panelSize.w,
          height: panelSize.h,
          zIndex: 90,
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 text-[15px] font-medium text-white/90 truncate" title={scriptTitle}>
            {scriptTitle}
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(false);
            }}
            title="还原为底部面板"
            className="shrink-0 text-white/40 hover:text-white/70 transition-colors cursor-pointer"
          >
            <Minimize2 size={16} />
          </button>
        </div>

        {upstreamTextNodes.length > 0 && (
          <div className="flex gap-2 mt-3">{upstreamCards}</div>
        )}

        {/* v1.4.19 展开态编辑区：同一套 PromptEditor 撑满剩余高度（@ 引用上游文本与其他面板同交互） */}
        <div className="flex-1 min-h-0 mt-3 flex flex-col">
          <PromptEditor
            {...editorProps}
            editorWrapClassName="flex-1 min-h-0"
            editorStyle={{ height: '100%', minHeight: 0, maxHeight: 'none' }}
          />
        </div>

        <div className="mt-3">{footer}</div>

        {/* 右下角拖拽手柄：nwse-resize，最大视口 92%（图二上限） */}
        <div
          onMouseDown={handleResizeStart}
          title="拖拽调整面板尺寸"
          className="absolute bottom-0 right-0 w-5 h-5 cursor-nwse-resize flex items-end justify-end p-1 text-white/30 hover:text-white/60 transition-colors"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <path d="M9 1 L1 9" />
            <path d="M9 5 L5 9" />
            <path d="M9 8 L8 9" />
          </svg>
        </div>
      </div>
      </>,
      document.body,
    );
  }

  return (
    <div
      className="nodrag nowheel w-[720px] rounded-2xl bg-[#262626]/95 border border-white/10 p-3 backdrop-blur-sm shadow-xl"
      onMouseDown={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between">
        {/* P2 接线点：参考图上传（加入 inputImages 通道） */}
        <button
          type="button"
          disabled
          className="flex items-center gap-1 px-2 py-1 rounded-md bg-white/5 text-[12px] text-white/50 cursor-not-allowed"
        >
          <Plus size={12} />
          参考图
        </button>
        {/* v1.4.17 全屏按钮：切换到 Portal 居中展开面板 */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(true);
          }}
          className="text-white/40 hover:text-white/70 transition-colors cursor-pointer"
          title="全屏"
        >
          <Maximize2 size={15} />
        </button>
      </div>

      {upstreamTextNodes.length > 0 && (
        <div className="flex gap-2 mt-2">{upstreamCards}</div>
      )}

      {/* v1.4.19 内嵌编辑区：统一 PromptEditor（默认 80/120px 自适应高度），@ 呼出上游文本引用菜单 */}
      <div className="mt-2">
        <PromptEditor {...editorProps} />
      </div>

      <div className="mt-2">{footer}</div>
    </div>
  );
});
ScriptQuickInput.displayName = 'ScriptQuickInput';
